/**
 * Chat Vuex module — state + actions for ERP user-to-user chat.
 *
 * State is hydrated from REST on first widget open and kept in sync via
 * SignalR events delivered through getSignalrManager(). Actions try to
 * use the hub (when connected) and fall back to REST.
 */
import api from "@/helpers/api";
import { getSignalrManager } from "@/helpers/signalrConnection";

let _hubBound = false;

export const state = {
  // Map<conversationId, ConversationDto>
  conversationsById: {},
  // Map<conversationId, MessageDto[]> chronological ascending
  messagesByConvoId: {},
  // Map<conversationId, number>
  unreadByConvoId: {},
  // Set<userId> currently online (best-effort)
  onlineUserIds: [],
  // Map<conversationId, { userId, until }> (until: epoch ms)
  typingByConvoId: {},
  // Currently selected conversation id (for widget pane state)
  selectedConversationId: null,
  loaded: false,
  // Bumped each time an incoming (not-own) message arrives that the user
  // hasn't acknowledged (selected conversation differs OR widget closed).
  // ChatWidget watches this to trigger FAB shake + beep + vibration.
  lastIncomingAt: 0,
};

export const getters = {
  sortedConversations(state) {
    return Object.values(state.conversationsById)
      .slice()
      .sort((a, b) => {
        const ta = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
        const tb = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
        return tb - ta;
      });
  },
  totalUnread(state) {
    return Object.values(state.unreadByConvoId).reduce((s, n) => s + (n || 0), 0);
  },
  isOnline: (state) => (userId) => state.onlineUserIds.includes(userId),
  selectedConversation(state) {
    return state.selectedConversationId
      ? state.conversationsById[state.selectedConversationId] || null
      : null;
  },
  messagesFor: (state) => (conversationId) => state.messagesByConvoId[conversationId] || [],
  isTyping: (state) => (conversationId) => {
    const t = state.typingByConvoId[conversationId];
    return t && t.until > Date.now() ? t.userId : null;
  },
};

export const mutations = {
  SET_CONVERSATIONS(state, list) {
    const map = {};
    const unread = {};
    for (const c of list) {
      map[c.id] = c;
      unread[c.id] = c.unreadCount || 0;
    }
    state.conversationsById = map;
    state.unreadByConvoId = unread;
    state.loaded = true;
  },
  UPSERT_CONVERSATION(state, conv) {
    state.conversationsById = { ...state.conversationsById, [conv.id]: conv };
    if (state.unreadByConvoId[conv.id] === undefined) {
      state.unreadByConvoId = { ...state.unreadByConvoId, [conv.id]: conv.unreadCount || 0 };
    }
  },
  SET_MESSAGES(state, { conversationId, messages }) {
    state.messagesByConvoId = { ...state.messagesByConvoId, [conversationId]: messages };
  },
  PREPEND_MESSAGES(state, { conversationId, messages }) {
    const existing = state.messagesByConvoId[conversationId] || [];
    const merged = [...messages, ...existing];
    state.messagesByConvoId = { ...state.messagesByConvoId, [conversationId]: merged };
  },
  APPEND_MESSAGE(state, msg) {
    const list = state.messagesByConvoId[msg.conversationId] || [];
    if (list.find((m) => m.id === msg.id)) return; // dedup
    state.messagesByConvoId = {
      ...state.messagesByConvoId,
      [msg.conversationId]: [...list, msg],
    };
  },
  SET_UNREAD(state, { conversationId, count }) {
    state.unreadByConvoId = { ...state.unreadByConvoId, [conversationId]: count };
  },
  BUMP_UNREAD(state, { conversationId, delta }) {
    const cur = state.unreadByConvoId[conversationId] || 0;
    state.unreadByConvoId = { ...state.unreadByConvoId, [conversationId]: Math.max(0, cur + delta) };
  },
  SET_LAST_MESSAGE_AT(state, { conversationId, lastMessageAt }) {
    const c = state.conversationsById[conversationId];
    if (!c) return;
    state.conversationsById = {
      ...state.conversationsById,
      [conversationId]: { ...c, lastMessageAt },
    };
  },
  SET_SELECTED(state, conversationId) {
    state.selectedConversationId = conversationId;
  },
  SET_USER_ONLINE(state, userId) {
    if (!state.onlineUserIds.includes(userId)) {
      state.onlineUserIds = [...state.onlineUserIds, userId];
    }
  },
  SET_USER_OFFLINE(state, userId) {
    state.onlineUserIds = state.onlineUserIds.filter((x) => x !== userId);
  },
  SET_TYPING(state, { conversationId, userId, isTyping }) {
    if (isTyping) {
      // Auto-expire typing after 4 seconds — clients should re-fire periodically.
      state.typingByConvoId = {
        ...state.typingByConvoId,
        [conversationId]: { userId, until: Date.now() + 4000 },
      };
    } else {
      const next = { ...state.typingByConvoId };
      delete next[conversationId];
      state.typingByConvoId = next;
    }
  },
  PING_INCOMING(state) {
    state.lastIncomingAt = Date.now();
  },
};

export const actions = {
  async bootstrap({ commit, dispatch, state }) {
    // Bind hub events once per browser tab.
    if (!_hubBound) {
      const sigr = getSignalrManager();
      sigr.on("erp-chat", "chat.message.new", (msg) => dispatch("_onMessageNew", msg));
      sigr.on("erp-chat", "chat.message.read", (e) => dispatch("_onMessageRead", e));
      sigr.on("erp-chat", "chat.typing", (e) => commit("SET_TYPING", e));
      sigr.on("erp-chat", "chat.conversation.updated", (e) => dispatch("_onConversationUpdated", e));
      sigr.on("erp-chat", "presence.online", (e) => commit("SET_USER_ONLINE", e.userId));
      sigr.on("erp-chat", "presence.offline", (e) => commit("SET_USER_OFFLINE", e.userId));
      _hubBound = true;
    }
    if (state.loaded) return;
    try {
      const { data } = await api.get("/erp-chat/conversations");
      commit("SET_CONVERSATIONS", data.data || []);
    } catch {
      // Non-critical; widget still functional via direct user search.
    }
  },

  async selectConversation({ commit, dispatch }, conversationId) {
    commit("SET_SELECTED", conversationId);
    await dispatch("loadMessages", conversationId);
    await dispatch("markRead", conversationId);
  },

  async loadMessages({ commit }, conversationId) {
    const { data } = await api.get(`/erp-chat/conversations/${conversationId}/messages`, {
      params: { pageSize: 30 },
    });
    commit("SET_MESSAGES", { conversationId, messages: data.data || [] });
  },

  async loadOlderMessages({ commit, state }, conversationId) {
    const list = state.messagesByConvoId[conversationId] || [];
    const oldest = list[0];
    if (!oldest) return;
    const { data } = await api.get(`/erp-chat/conversations/${conversationId}/messages`, {
      params: { before: oldest.id, pageSize: 30 },
    });
    if ((data.data || []).length > 0) {
      commit("PREPEND_MESSAGES", { conversationId, messages: data.data });
    }
  },

  async createDirect({ commit, dispatch }, otherUserId) {
    const { data } = await api.post("/erp-chat/conversations", {
      type: 0,
      participantUserIds: [otherUserId],
    });
    commit("UPSERT_CONVERSATION", data);
    await dispatch("selectConversation", data.id);
    return data;
  },

  async createGroup({ commit, dispatch }, { name, userIds }) {
    const { data } = await api.post("/erp-chat/conversations", {
      type: 1,
      name,
      participantUserIds: userIds,
    });
    commit("UPSERT_CONVERSATION", data);
    await dispatch("selectConversation", data.id);
    return data;
  },

  async sendMessage({ commit }, { conversationId, body, attachmentId, mentions }) {
    const sigr = getSignalrManager();
    const mentionsJson = mentions && mentions.length ? JSON.stringify(mentions) : null;
    if (sigr.isLeader) {
      try {
        await sigr.invoke("erp-chat", "SendMessage", conversationId, body || null, attachmentId || null, mentionsJson);
        // The "chat.message.new" event will arrive and append the message.
        return;
      } catch {
        // Fall through to REST.
      }
    }
    const { data: dto } = await api.post(`/erp-chat/conversations/${conversationId}/messages`, {
      body,
      attachmentId: attachmentId || null,
      mentionsJson,
    });
    commit("APPEND_MESSAGE", dto);
    commit("SET_LAST_MESSAGE_AT", { conversationId, lastMessageAt: dto.sentAt });
    return dto;
  },

  async markRead({ commit, state }, conversationId) {
    const list = state.messagesByConvoId[conversationId] || [];
    const last = list[list.length - 1];
    if (!last) {
      commit("SET_UNREAD", { conversationId, count: 0 });
      return;
    }
    commit("SET_UNREAD", { conversationId, count: 0 });
    const sigr = getSignalrManager();
    if (sigr.isLeader) {
      try {
        await sigr.invoke("erp-chat", "MarkRead", conversationId, last.id);
        return;
      } catch { /* fall through */ }
    }
    // No dedicated REST endpoint — server advances LastReadMessageId via hub.
    // For follower tabs, the next visit by the leader will sync.
  },

  async typing(_ctx, { conversationId, isTyping }) {
    const sigr = getSignalrManager();
    if (sigr.isLeader) {
      try { await sigr.invoke("erp-chat", "Typing", conversationId, isTyping); } catch { /* ignore */ }
    }
  },

  // ── Hub event handlers (private) ─────────────────────────────────────

  _onMessageNew({ commit, state }, msg) {
    const list = state.messagesByConvoId[msg.conversationId] || [];
    const isDuplicate = list.some((m) => m.id === msg.id);
    commit("APPEND_MESSAGE", msg);
    commit("SET_LAST_MESSAGE_AT", { conversationId: msg.conversationId, lastMessageAt: msg.sentAt });

    const myUserId = Number(localStorage.getItem("userid") || 0);
    const isOwn = msg.senderUserId === myUserId;
    const isViewing = state.selectedConversationId === msg.conversationId;

    if (!isViewing) {
      commit("BUMP_UNREAD", { conversationId: msg.conversationId, delta: 1 });
    }
    // Trigger UI alert (FAB shake + beep) only when an incoming message
    // arrives that the user is NOT actively viewing. Skip own messages
    // and skip duplicates that were already appended optimistically.
    if (!isOwn && !isViewing && !isDuplicate) {
      commit("PING_INCOMING");
    }
  },

  // Read-receipt UI is not rendered in V1; placeholder kept so the hub
  // subscription doesn't error if the event arrives.
  _onMessageRead() {
    /* reserved for Phase 6 read-receipt UI */
  },

  async _onConversationUpdated({ state, commit }, e) {
    if (state.conversationsById[e.conversationId]) {
      commit("SET_LAST_MESSAGE_AT", { conversationId: e.conversationId, lastMessageAt: e.lastMessageAt });
      return;
    }
    // We received an update for a conversation we don't know about — fetch it.
    try {
      const { data } = await api.get(`/erp-chat/conversations/${e.conversationId}`);
      commit("UPSERT_CONVERSATION", data);
    } catch { /* ignore */ }
  },
};

export const namespaced = true;
